import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { fetchSuggestions } from '../util/algoliaSuggestions';

/**
 * Drives the search type-ahead dropdown (RAS-2536).
 *
 * Wraps util/algoliaSuggestions with everything a live input needs and that module
 * deliberately has none of: debouncing, a cache, stale-response protection, and the
 * open/highlight state the dropdown renders from.
 *
 * The highlight index lives here rather than in the component because it has to reset every
 * time the suggestion list changes — keeping the two apart is how you end up with the arrow
 * keys pointing at a row that no longer exists.
 */

// Long enough to coalesce a burst of typing, short enough that the list feels live. Note this
// is unrelated to the ~900ms debounce on the search box itself, which commits a full search.
const DEBOUNCE_MS = 200;

const MIN_QUERY_LENGTH = 2;
const CACHE_LIMIT = 50;

// ~1.1km. Rounding the location means nudging the map does not invalidate the cache, while
// moving to a different area does.
const GEO_PRECISION = 2;

const NO_HIGHLIGHT = -1;

const bucketGeo = aroundLatLng => {
  if (!aroundLatLng) return 'none';
  const [lat, lng] = String(aroundLatLng).split(',').map(n => Number(n.trim()));
  if (!Number.isFinite(lat) || !Number.isFinite(lng)) return 'none';
  return `${lat.toFixed(GEO_PRECISION)},${lng.toFixed(GEO_PRECISION)}`;
};

/**
 * @param {Object} params
 * @param {string} params.query - current input value
 * @param {string} [params.locale='en']
 * @param {string} [params.aroundLatLng] - "lat,lng" of the selected search location
 * @param {string} [params.filters] - store search passes its own baseFilters
 * @param {boolean} [params.useLocation=true] - false for store search
 * @param {number} [params.limit=6]
 * @param {boolean} [params.enabled=true]
 * @param {number} [params.extraOptions=0] - rows the dropdown renders that are not
 *   suggestions, so they are still reachable by keyboard. The "See all results" row is one.
 */
export const useSearchSuggestions = ({
  query,
  locale = 'en',
  aroundLatLng = null,
  filters,
  useLocation = true,
  limit = 6,
  enabled = true,
  extraOptions = 0,
} = {}) => {
  const [suggestions, setSuggestions] = useState([]);
  const [highlightedIndex, setHighlightedIndex] = useState(NO_HIGHLIGHT);
  const [isDismissed, setIsDismissed] = useState(false);

  const cacheRef = useRef(new Map());
  const timerRef = useRef(null);
  // The query the list was last dismissed for. Selecting a suggestion writes that suggestion
  // into the input, which otherwise reads as fresh typing — the list would reopen showing the
  // term the customer just chose, and spend another request fetching it.
  const dismissedForQueryRef = useRef(null);
  // Monotonic id so a slow response for an earlier query cannot overwrite a newer one.
  const requestIdRef = useRef(0);
  const isMountedRef = useRef(true);
  const isComposingRef = useRef(false);

  const trimmed = String(query == null ? '' : query).trim();
  const cacheKey = useMemo(
    () => [locale, bucketGeo(useLocation ? aroundLatLng : null), filters || '', limit, trimmed].join('|'),
    [locale, aroundLatLng, useLocation, filters, limit, trimmed]
  );

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, []);

  const applyResults = useCallback((results, requestId) => {
    // Both guards matter: unmounted means nobody is listening, stale means a newer keystroke
    // has already superseded this response.
    if (!isMountedRef.current || requestId !== requestIdRef.current) return;
    setSuggestions(results);
    setHighlightedIndex(NO_HIGHLIGHT);
  }, []);

  useEffect(() => {
    if (timerRef.current) clearTimeout(timerRef.current);

    if (!enabled || trimmed.length < MIN_QUERY_LENGTH) {
      // Invalidate any in-flight response as well, or clearing the box could be undone a
      // moment later by a request that was already on its way.
      requestIdRef.current += 1;
      setSuggestions([]);
      setHighlightedIndex(NO_HIGHLIGHT);
      return undefined;
    }

    // Dismissal sticks to the exact text it was dismissed for. Typing on is a new chance to
    // show the list; having a suggestion written into the input is not.
    if (dismissedForQueryRef.current === trimmed) return undefined;
    dismissedForQueryRef.current = null;
    setIsDismissed(false);

    const cached = cacheRef.current.get(cacheKey);
    if (cached) {
      requestIdRef.current += 1;
      setSuggestions(cached);
      setHighlightedIndex(NO_HIGHLIGHT);
      return undefined;
    }

    timerRef.current = setTimeout(async () => {
      // Mid-composition text is not a query — an IME emits provisional characters on the way
      // to the intended word, and each one would spend a request on something nobody typed.
      if (isComposingRef.current) return;

      const requestId = (requestIdRef.current += 1);
      const results = await fetchSuggestions({
        query: trimmed,
        locale,
        aroundLatLng,
        filters,
        useLocation,
        limit,
      });

      // Cache before the staleness check: the result is valid for this key regardless of
      // whether the customer has since typed on.
      const cache = cacheRef.current;
      cache.set(cacheKey, results);
      if (cache.size > CACHE_LIMIT) cache.delete(cache.keys().next().value);

      applyResults(results, requestId);
    }, DEBOUNCE_MS);

    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [cacheKey, enabled, trimmed, locale, aroundLatLng, filters, useLocation, limit, applyResults]);

  /**
   * @param {string} [forQuery] - the text the dismissal covers. Defaults to what is currently
   *   in the box (Escape), but selecting a suggestion must pass the SELECTED value: the input
   *   has not been updated yet at that point, so defaulting would record the half-typed text
   *   and the list would reopen the moment the selection landed.
   */
  const close = useCallback(
    forQuery => {
      dismissedForQueryRef.current =
        typeof forQuery === 'string' ? forQuery.trim() : trimmed;
      setIsDismissed(true);
      setHighlightedIndex(NO_HIGHLIGHT);
    },
    [trimmed]
  );

  const reset = useCallback(() => {
    requestIdRef.current += 1;
    dismissedForQueryRef.current = null;
    setSuggestions([]);
    setHighlightedIndex(NO_HIGHLIGHT);
    setIsDismissed(false);
  }, []);

  const optionCount = suggestions.length ? suggestions.length + extraOptions : 0;

  const moveHighlight = useCallback(
    delta =>
      setHighlightedIndex(current => {
        if (!optionCount) return NO_HIGHLIGHT;
        const next = current + delta;
        // Wraps at both ends, and stepping up from "nothing highlighted" lands on the last row.
        if (next < 0) return optionCount - 1;
        if (next >= optionCount) return NO_HIGHLIGHT;
        return next;
      }),
    [optionCount]
  );

  // Spread onto the input so an IME composition suppresses fetching until it completes.
  const compositionHandlers = useMemo(
    () => ({
      onCompositionStart: () => {
        isComposingRef.current = true;
      },
      onCompositionEnd: () => {
        isComposingRef.current = false;
      },
    }),
    []
  );

  return {
    suggestions,
    isOpen: !isDismissed && suggestions.length > 0,
    highlightedIndex,
    // Null while an extra row (e.g. "See all results") is highlighted — there is no suggestion
    // to select, so Enter falls through to the caller's normal "search what was typed" path,
    // which is exactly what that row does anyway.
    highlighted: highlightedIndex >= 0 ? suggestions[highlightedIndex] || null : null,
    setHighlightedIndex,
    moveHighlight,
    close,
    reset,
    compositionHandlers,
  };
};

export const __testing = { DEBOUNCE_MS, MIN_QUERY_LENGTH, CACHE_LIMIT, NO_HIGHLIGHT };
